import React, { useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import { Container, Tab } from 'react-bootstrap';
import { Link } from 'react-router-dom';
import { Table, ActionLink, Label, Tag, Input, Fieldset, Radios, BackLink, Card, Details, Button, Select } from 'nhsuk-react-components';
import { listPatients, deletePatient } from '../../actions/patientActions';

import Pagination from '../../components/pagination/Pagination.component';

import AppSpinner from '../../components/spinner/AppSpinner.component';
import { formatISODate } from '../../utils/functions';
import PageWrapper from '../../components/layout/PageWrapper.component';

export default function PatientListScreen({ history }) {

    const dispatch = useDispatch();

    useEffect(() => {
        document.title = "Patients";
    }, []);


    const patientList = useSelector((state) => state.patientList);
    const { loading, error, patients, currentPage, totalPages, totalCount } = patientList;

    // set initial filter to empty string, set default value 
    // {{ _.docker_api_url }}/api/v1/patient/all?page=1&limit=2&filter=Email&query=gmail
    const [patientListFilter, setpatientListFilter] = useState({ query: '', page: 1, filter: 'Surname' });

    // listen to filter changes
    useEffect(() => {
        dispatch(listPatients(patientListFilter));
    }, [dispatch, patientListFilter]);

    // handle page change
    const handlePageChange = (newPage) => {
        setpatientListFilter({ ...patientListFilter, page: newPage });
    };

    // handle search text field change
    const handleSearchChange = (e) => {
        setpatientListFilter({ ...patientListFilter, query: e.target.value });
    };

    // handle filter value change
    const handleFilterChange = (e) => {
        const selectedFilter = e.target.value;
        setpatientListFilter({ ...patientListFilter, filter: selectedFilter });
    };

    // Handle Delete
    const handleDelete = (id) => {
        if (window.confirm('Are you sure you want to delete this user?')) {
            dispatch(deletePatient(id));
            setpatientListFilter({ query: '', page: 1, filter: 'Surname' });
        }
    }

    return (
        <React.Fragment>
            <div className="page-content">
                <Container fluid>
                    <div className="nhsuk-grid-row">
                        <PageWrapper title="Patient Management" totalCount={totalCount} loading={loading}>
                            <BackLink
                                asElement="a"
                                href="#"
                                onClick={() => history.goBack()}
                            >
                                Return back
                            </BackLink>
                        </PageWrapper>
                        <div className="nhsuk-grid-column-full">
                            <div className="welcome-box">
                                <Button
                                    variant="primary"
                                    asElement={Link}
                                    onClick={() => history.push(`/patients/create`)}
                                    className="nhsuk-u-margin-bottom-4"
                                >
                                    + Add Patient
                                </Button>

                                <Card>
                                    <Card.Content>
                                        <Card.Heading className="nhsuk-heading-m">
                                            <div className="nhsuk-grid-row">
                                                <div className="nhsuk-grid-column-two-thirds">
                                                    Patient Records
                                                </div>
                                            </div>
                                        </Card.Heading>
                                        
                                        {/* Search and Filter Controls */}
                                        <div className="nhsuk-grid-row">
                                            <div className="nhsuk-grid-column-two-thirds">
                                                <Input
                                                    id="search-patients"
                                                    label="Search Patients"
                                                    name="search-patients"
                                                    type="search"
                                                    onKeyDown={(e) => {
                                                        if (e.key === "Enter") {
                                                            handleSearchChange(e);
                                                        }
                                                    }}
                                                />
                                            </div>
                                            <div className="nhsuk-grid-column-one-third">
                                                <div
                                                    style={{
                                                        display: 'flex',
                                                        alignItems: 'center',
                                                        gap: '8px',
                                                        flexWrap: 'wrap',
                                                        maxWidth: '100%',
                                                        overflow: 'hidden'
                                                    }}
                                                    className="nhsuk-u-margin-bottom-2"
                                                >
                                                    <Select
                                                        id="filter-patients"
                                                        label="Filter by"
                                                        name="filter-patients"
                                                        onChange={handleFilterChange}
                                                        value={patientListFilter.filter}
                                                        style={{ flex: '1 1 auto', minWidth: '150px', maxWidth: '200px', marginBottom: '8px' }}
                                                    >
                                                        <Select.Option value="Surname">Surname</Select.Option>
                                                        <Select.Option value="Forename">First Name</Select.Option>
                                                        <Select.Option value="NHSNumber">NHS Number</Select.Option>
                                                        <Select.Option value="DateOfBirth">Date of Birth</Select.Option>
                                                        <Select.Option value="ListName">List Name</Select.Option>
                                                        <Select.Option value="ImplantCentre">Implant Centre(s)</Select.Option>
                                                        <Select.Option value="FollowUpCentre">Follow-up Centre(s)</Select.Option>
                                                    </Select>
                                                    <Button
                                                        secondary
                                                        className="nhsuk-u-margin-left-2"
                                                        onClick={() => setpatientListFilter({ query: '', page: 1, filter: 'Surname' })}
                                                        style={{
                                                            height: '40px',
                                                            padding: '0 8px',
                                                            marginTop: '30px',
                                                            flex: '1 1 auto',
                                                            minWidth: '80px',
                                                            maxWidth: '120px',
                                                            fontSize: '14px'
                                                        }}
                                                    >
                                                        Clear
                                                    </Button>
                                                    <Button
                                                        className="nhsuk-u-margin-left-2"
                                                        onClick={() => dispatch(listPatients(patientListFilter))}
                                                        style={{
                                                            height: '40px',
                                                            padding: '0 8px',
                                                            marginTop: '30px',
                                                            flex: '1 1 auto',
                                                            minWidth: '80px',
                                                            maxWidth: '120px',
                                                            fontSize: '14px'
                                                        }}
                                                    >
                                                        Refresh
                                                    </Button>
                                                </div>
                                            </div>
                                        </div>
                                        
                                        {/* Patients Table */}
                                        {loading ? (
                                            <AppSpinner />
                                        ) : error ? (
                                            <p>{error}</p>
                                        ) : (
                                            <>
                                                <Table responsive className="nhsuk-u-margin-top-3 compact-admin-table">
                                                    <Table.Head>
                                                        <Table.Row>
                                                            <Table.Cell>Patient</Table.Cell>
                                                            <Table.Cell>Date of Birth</Table.Cell>
                                                            <Table.Cell>NHS / Id</Table.Cell>
                                                            <Table.Cell>Implant Centre(s)</Table.Cell>
                                                            <Table.Cell>Follow-up Centre(s)</Table.Cell>
                                                            <Table.Cell>List Name</Table.Cell>
                                                            <Table.Cell>Actions</Table.Cell>
                                                        </Table.Row>
                                                    </Table.Head>
                                                    <Table.Body>
                                                        {patients && patients.length > 0 ? (
                                                            patients.map((patient) => (
                                                                <Table.Row key={patient.id}>
                                                                    <Table.Cell>
                                                                        <strong>{patient.forename} {patient.surname}</strong>
                                                                    </Table.Cell>
                                                                    <Table.Cell>{formatISODate(patient.dateOfBirth)}</Table.Cell>
                                                                    <Table.Cell>{patient.nhsNumber || patient.identificationNumber || '—'}</Table.Cell>
                                                                    <Table.Cell>{centreLabels(patient.patientImplantCentres, (link) => link.implantCentre)}</Table.Cell>
                                                                    <Table.Cell>{centreLabels(patient.patientFollowUpCentres, (link) => link.followUpCentre)}</Table.Cell>
                                                                    <Table.Cell>
                                                                        {patient.listName ? (
                                                                            <Tag>{patient.listName}</Tag>
                                                                        ) : (
                                                                            "-"
                                                                        )}
                                                                    </Table.Cell>
                                                                    <Table.Cell>
                                                                        <div className="compact-admin-table__actions">
                                                                            <Button
                                                                                onClick={() => history.push(`/patient/${patient.id}`)}
                                                                                className="nhsuk-button--small"
                                                                            >
                                                                                View
                                                                            </Button>
                                                                            <Button
                                                                                secondary
                                                                                onClick={() => handleDelete(patient.id)}
                                                                                className="nhsuk-button--small btn-delete"
                                                                            >
                                                                                Delete
                                                                            </Button>
                                                                        </div>
                                                                    </Table.Cell>
                                                                </Table.Row>
                                                            ))
                                                        ) : (
                                                            <Table.Row>
                                                                <Table.Cell colSpan="7">
                                                                    <div>No patients found. Try adjusting your search or adding a new patient.</div>
                                                                </Table.Cell>
                                                            </Table.Row>
                                                        )}
                                                    </Table.Body>
                                                </Table>
                                            </>
                                        )}
                                    </Card.Content>
                                </Card>

                                {!loading && !error && patients && patients.length > 0 && (
                                    <Pagination
                                        currentPage={currentPage}
                                        totalPages={totalPages}
                                        handlePageChange={handlePageChange}
                                    />
                                )}
                            </div>
                        </div>
                    </div>
                </Container>
            </div>
        </React.Fragment>

    );
}

function centreLabels(links, centreOf) {
    const centres = (links || [])
        .map(centreOf)
        .filter((centre) => centre && (centre.odsCode || centre.name));
    if (!centres.length) {
        return '—';
    }

    return (
        <span className="centre-labels">
            {centres.map((centre, index) => {
                const odsCode = (centre.odsCode || '').trim();
                const label = odsCode ? <Tag>{odsCode}</Tag> : centre.name;
                return (
                    <span key={`${odsCode || centre.name}-${index}`}>
                        {index > 0 ? ', ' : null}
                        {label}
                    </span>
                );
            })}
        </span>
    );
}